export function useDrawTools(ctx) {
    const vm = ctx.vm;
    const onCtxEditAnnotation = () => {
        const id = vm.ctx.id;
        if (!id) return;
        const meta = vm.overlayMeta.get(id);
        if (!meta || meta.name !== "kiteAnnotation") return;
        const p0 = meta.points?.[0];
        if (!p0) return;
        const px = vm.pointToPixel(p0);
        if (!px) return;
        const root = ctx.rootEl();
        const maxLeft = Math.max(0, (root.clientWidth || 0) - 240);
        const maxTop = Math.max(0, (root.clientHeight || 0) - 40);
        const left = Math.max(0, Math.min(px.x + 6, maxLeft));
        const top = Math.max(0, Math.min(px.y - 12, maxTop));
        vm.annotationMode = true;
        vm.ann.open = true;
        vm.ann.point = { timestamp: p0.timestamp, value: p0.value };
        vm.ann.text = String(meta.extendData?.text || "");
        vm.ann.editingId = id;
        vm.placeAnnBox(left, top);
        vm.closeCtx();
        ctx.nextTick(() => setTimeout(() => ctx.refs().annInput?.focus?.(), 0));
    };
    const cancelAnnotation = () => {
        vm.ann.open = false;
        vm.ann.text = "";
        vm.ann.point = null;
        vm.ann.editingId = null;
        vm.annotationMode = false;
        vm.pendingTextTool = null;
        vm.pendingCallout = null;
        try { ctx.chart().setScrollEnabled?.(true); } catch (_) { }
        try { ctx.chart().setZoomEnabled?.(true); } catch (_) { }
    };
    const saveAnnotation = () => {
        const chart = ctx.chart();
        if (!chart) return;
        const txt = String(vm.ann.text || "").trim();
        const point = vm.ann.point;
        if (!txt || !point) return cancelAnnotation();
        if (vm.ann.editingId) {
            const id = vm.ann.editingId;
            const meta = vm.overlayMeta.get(id);
            if (meta) {
                const nextExtend = { ...(meta.extendData || {}), text: txt };
                meta.extendData = nextExtend;
                chart.overrideOverlay?.({ id, extendData: nextExtend, points: meta.points });
            }
            return cancelAnnotation();
        }
        try {
            vm.createOverlayTracked({
                name: "kiteAnnotation",
                paneId: ctx.getMainPaneId(),
                points: [{ timestamp: point.timestamp, value: point.value }],
                extendData: { text: txt },
                zLevel: 10000,
            });
        } finally {
            cancelAnnotation();
        }
    };
    const hitTestCrossGroup = (mx, my) => {
        const entries = Array.from(vm.overlayMeta.entries());
        for (let i = entries.length - 1; i >= 0; i--) {
            const [, meta] = entries[i];
            if (!meta?.groupId) continue;
            if (!["horizontalStraightLine", "verticalStraightLine", "kiteCrosslineTag"].includes(meta.name)) continue;
            const p0 = meta.points?.[0];
            if (!p0) continue;
            const px = vm.pointToPixel(p0);
            if (!px) continue;
            const r = 10;
            if (vm.dist2(mx, my, px.x, px.y) <= r * r) return meta.groupId;
        }
        return null;
    };
    const onCrossDragDown = (e) => {
        if (e.button !== 0) return;
        if (!ctx.chart()) return;
        const box = ctx.rootEl().getBoundingClientRect();
        const mx = e.clientX - box.left;
        const my = e.clientY - box.top;
        const gid = hitTestCrossGroup(mx, my);
        if (!gid) return;
        vm.crossDrag.active = true;
        vm.crossDrag.groupId = gid;
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
    };
    const onCrossDragMove = (e) => {
        if (!vm.crossDrag.active) return;
        const box = ctx.rootEl().getBoundingClientRect();
        const mx = e.clientX - box.left;
        const my = e.clientY - box.top;
        const picked = vm.pickPointFromMouse(mx, my);
        if (!picked) return;
        const nextPoint = { timestamp: picked.timestamp, value: picked.value };
        const bars = vm.getBarsSafe();
        const bar = bars?.[picked.dataIndex];
        const ts = bar?.timestamp ?? picked.timestamp;
        for (const [id, meta] of vm.overlayMeta.entries()) {
            if (meta.groupId !== vm.crossDrag.groupId) continue;
            if (meta.name === "kiteCrosslineTag") {
                const nextExtend = {
                    ...(meta.extendData || {}),
                    priceText: Number(picked.value).toFixed(2),
                    timeText: new Date(ts).toISOString().slice(0, 10),
                };
                meta.points = [nextPoint];
                meta.extendData = nextExtend;
                ctx.chart().overrideOverlay?.({ id, points: [nextPoint], extendData: nextExtend });
            } else {
                meta.points = [nextPoint];
                ctx.chart().overrideOverlay?.({ id, points: [nextPoint] });
            }
        }
    };
    const onCrossDragUp = () => {
        vm.crossDrag.active = false;
        vm.crossDrag.groupId = null;
    };
    const resetFreehand = () => {
        vm.freehand.tool = "";
        vm.freehand.drawing = false;
        vm.freehand.id = null;
        vm.freehand.points = [];
        vm.freehand.lastPx = null;
        vm.freehand.pendingPx = null;
        if (vm.freehand.raf) cancelAnimationFrame(vm.freehand.raf);
        vm.freehand.raf = 0;
    };
    const onFreehandDown = (e) => {
        if (!ctx.chart()) return;
        if (e.button !== 0) return;
        if (vm.freehand.tool !== "kiteDoodle") return;
        if (vm.stickerDrag?.active) return;
        if (vm.calloutDrag?.active) return;
        const box = ctx.rootEl().getBoundingClientRect();
        const mx = e.clientX - box.left;
        const my = e.clientY - box.top;
        const p = vm.pickPointFromMouse(mx, my);
        if (!p) return;
        vm.freehand.drawing = true;
        vm.freehand.points = [{ timestamp: p.timestamp, value: p.value }];
        vm.freehand.lastPx = { x: mx, y: my };
        const id = vm.createOverlayTracked({
            name: "kiteDoodle",
            paneId: ctx.getMainPaneId(),
            points: vm.freehand.points,
        });
        vm.freehand.id = id ?? null;
        e.preventDefault();
    };
    const onFreehandMove = (e) => {
        if (!vm.freehand.drawing) return;
        const id = vm.freehand.id;
        if (!id) return;
        const box = ctx.rootEl().getBoundingClientRect();
        const mx = e.clientX - box.left;
        const my = e.clientY - box.top;
        vm.freehand.pendingPx = { x: mx, y: my };
        if (vm.freehand.raf) return;
        vm.freehand.raf = requestAnimationFrame(() => {
            vm.freehand.raf = 0;
            const pp = vm.freehand.pendingPx;
            if (!pp) return;
            const last = vm.freehand.lastPx || pp;
            const dx = pp.x - last.x;
            const dy = pp.y - last.y;
            if (dx * dx + dy * dy < vm.freehand.minDist2) return;
            const p = vm.pickPointFromMouse(pp.x, pp.y);
            if (!p) return;
            vm.freehand.lastPx = pp;
            const pts = vm.freehand.points;
            pts.push({ timestamp: p.timestamp, value: p.value });
            if (pts.length > vm.freehand.maxPoints) pts.shift();
            ctx.chart().overrideOverlay?.({ id, points: pts });
            const meta = vm.overlayMeta.get(id);
            if (meta) meta.points = pts;
        });
    };
    const onFreehandUp = () => {
        if (!vm.freehand.drawing) return;
        vm.freehand.drawing = false;
        vm.freehand.id = null;
        vm.freehand.points = [];
        vm.freehand.lastPx = null;
        if (vm.freehand.raf) cancelAnimationFrame(vm.freehand.raf);
        vm.freehand.raf = 0;
    };
    const setStickerToolsVisible = (id, visible) => {
        const rec = vm.stickerStore.get(id);
        if (!rec) return;
        const nextExtend = { ...(rec.extend || {}), showTools: !!visible };
        rec.extend = nextExtend;
        ctx.chart().overrideOverlay?.({
            id,
            extendData: nextExtend,
            points: [rec.point],
        });
        const meta = vm.overlayMeta.get(id);
        if (meta) meta.extendData = JSON.parse(JSON.stringify(nextExtend));
    };
    const hitTestStickerBody = (mx, my) => {
        const entries = Array.from(vm.stickerStore.entries());
        for (let i = entries.length - 1; i >= 0; i--) {
            const [id, rec] = entries[i];
            const center = vm.pointToPixel(rec.point);
            if (!center) continue;
            const scale = Number(rec.extend?.scale ?? 1);
            const kind = String(rec.extend?.kind || "");
            const r = (kind === "check" || kind === "x" ? 90 : 45) * scale;
            if (vm.dist2(mx, my, center.x, center.y) <= r * r) return { id };
        }
        return null;
    };
    const hitTestArrow = (mx, my) => {
        const HIT = 12;
        const entries = Array.from(vm.stickerStore.entries());
        for (let i = entries.length - 1; i >= 0; i--) {
            const [id, rec] = entries[i];
            const center = vm.pointToPixel(rec.point);
            if (!center) continue;
            const scale = Number(rec.extend?.scale ?? 1);
            const angle = Number(rec.extend?.angle ?? 0);
            const rot = vm.localToWorld(center, { x: -55, y: -10 }, scale, angle);
            const res = vm.localToWorld(center, { x: -55, y: 30 }, scale, angle);
            if (vm.dist2(mx, my, rot.x, rot.y) <= HIT * HIT) return { id, mode: "rotate" };
            if (vm.dist2(mx, my, res.x, res.y) <= HIT * HIT) return { id, mode: "resize" };
            const r = 40 * scale;
            if (vm.dist2(mx, my, center.x, center.y) <= r * r) return { id, mode: "move" };
        }
        return null;
    };
    const onStickerHoverMove = (e) => {
        if (!ctx.chart()) return;
        if (vm.stickerDrag?.active) return;
        const box = ctx.rootEl().getBoundingClientRect();
        const mx = e.clientX - box.left;
        const my = e.clientY - box.top;
        const hit = hitTestStickerBody(mx, my);
        const nextId = hit?.id || null;
        if (nextId === vm.hoverStickerId) return;
        if (vm.hoverStickerId) setStickerToolsVisible(vm.hoverStickerId, false);
        if (nextId) setStickerToolsVisible(nextId, true);
        vm.hoverStickerId = nextId;
    };
    const onStickerHoverLeave = () => {
        if (vm.hoverStickerId) {
            setStickerToolsVisible(vm.hoverStickerId, false);
            vm.hoverStickerId = null;
        }
    };
    const updateSticker = (id, nextPoint, nextExtend) => {
        const rec = vm.stickerStore.get(id);
        if (!rec) return;
        const mergedExtend = { ...(rec.extend || {}), ...(nextExtend || {}) };
        rec.point = nextPoint;
        rec.extend = mergedExtend;
        ctx.chart().overrideOverlay?.({
            id,
            points: [nextPoint],
            extendData: mergedExtend,
        });
        const meta = vm.overlayMeta.get(id);
        if (meta) {
            meta.points = [JSON.parse(JSON.stringify(nextPoint))];
            meta.extendData = JSON.parse(JSON.stringify(mergedExtend));
        }
    };
    const onStickerMouseDown = (e) => {
        if (!ctx.chart()) return;
        if (e.button !== 0) return;
        if (!vm.stickerTool) return;
        const box = ctx.rootEl().getBoundingClientRect();
        const mx = e.clientX - box.left;
        const my = e.clientY - box.top;
        const hit = hitTestArrow(mx, my);
        if (hit) {
            const rec = vm.stickerStore.get(hit.id);
            if (!rec) return;
            vm.stickerDrag.active = true;
            vm.stickerDrag.id = hit.id;
            vm.stickerDrag.mode = hit.mode;
            vm.stickerDrag.centerPx = vm.pointToPixel(rec.point);
            e.preventDefault();
            return;
        }
        const point = vm.pickPointFromMouse(mx, my);
        if (!point) return;
        const overlayName = vm.stickerTool;
        const extend =
            overlayName === "kiteSticker"
                ? { kind: vm.stickerKind, scale: 1, angle: 0, showTools: false }
                : { scale: 0.6, angle: 0, showTools: false };
        const id = vm.createOverlayTracked({
            name: overlayName,
            paneId: ctx.getMainPaneId(),
            points: [{ timestamp: point.timestamp, value: point.value }],
            extendData: extend,
        });
        if (id == null) return;
        const p = { timestamp: point.timestamp, value: point.value };
        vm.stickerStore.set(id, { name: overlayName, point: p, extend });
        vm.stickerDrag.active = true;
        vm.stickerDrag.id = id;
        vm.stickerDrag.mode = "resize";
        vm.stickerDrag.centerPx = { x: mx, y: my };
        e.preventDefault();
    };
    const onStickerMouseMove = (e) => {
        if (!vm.stickerDrag.active) return;
        const id = vm.stickerDrag.id;
        const rec = vm.stickerStore.get(id);
        if (!rec) return;
        const box = ctx.rootEl().getBoundingClientRect();
        const mx = e.clientX - box.left;
        const my = e.clientY - box.top;
        const center = vm.stickerDrag.centerPx;
        if (!center) return;
        const dx = mx - center.x;
        const dy = my - center.y;
        if (vm.stickerDrag.mode === "move") {
            const p = vm.pickPointFromMouse(mx, my);
            if (!p) return;
            updateSticker(id, { timestamp: p.timestamp, value: p.value }, null);
            return;
        }
        if (vm.stickerDrag.mode === "resize") {
            const dist = Math.sqrt(dx * dx + dy * dy);
            const scale = Math.max(0.3, Math.min(6, dist / 60));
            updateSticker(id, rec.point, { scale });
            return;
        }
        if (vm.stickerDrag.mode === "rotate") {
            const angle = Math.atan2(dy, dx) - Math.PI / 2;
            updateSticker(id, rec.point, { angle });
        }
    };
    const onStickerMouseUp = () => {
        if (!vm.stickerDrag.active) return;
        vm.stickerDrag.active = false;
        vm.stickerDrag.id = null;
        vm.stickerDrag.mode = "";
        vm.stickerDrag.centerPx = null;
    };
    return {
        onCtxEditAnnotation,
        cancelAnnotation,
        saveAnnotation,
        hitTestCrossGroup,
        onCrossDragDown,
        onCrossDragMove,
        onCrossDragUp,
        resetFreehand,
        onFreehandDown,
        onFreehandMove,
        onFreehandUp,
        setStickerToolsVisible,
        hitTestStickerBody,
        hitTestArrow,
        onStickerHoverMove,
        onStickerHoverLeave,
        updateSticker,
        onStickerMouseDown,
        onStickerMouseMove,
        onStickerMouseUp,
    };
}
